import { reportClientMessage } from '@utils/clientErrors'
import { askEvaroLogClient } from '@services/client/discovery/log'
import { type CartItem, useCheckoutStore } from 'store/checkout'
import { usePatientStore } from 'store/patient'
import type { UpsellProduct, iProduct } from 'types/product'
import { isStorageAvailable } from './storage'
import { getChildCategories as getChildCategoriesUtil, logWithCategoriesReady } from './category'
import { getSubscriptionPeriodLabel } from './product'
import type { ResolvedMenuLink } from './vendorMenu'
import type { MixedCartConflictType } from './mixedCart'

const formatItem = (item: any, options?: { item_list_id?: string; item_list_name?: string }) => {
  const listInfo = options?.item_list_id ? options : getStoredItemListInfo(item)
  let variation
  let item_name
  if ('variation' in item) {
    variation =
      item.variation?.find((e: { attribute: string; label: string }) => e.attribute === 'pa_ep_frequency')?.label ??
      getSubscriptionPeriodLabel(item.subscription) ??
      'One Time Purchase'
    item_name = `${item.title} [${item.variation?.map((e: { label: string }) => e.label).join(', ')}]`.toLowerCase()
  } else if ('variations' in item) {
    item_name = `${item.name} [${Object.values(item.variations)
      .filter((value): value is string => typeof value === 'string')
      .join(', ')}]`.toLowerCase()
    variation = item.variations.frequency ?? getSubscriptionPeriodLabel(item.variations.subscription)
  } else if ('title' in item) {
    item_name = item.title
  }

  // Get categories from item
  const categories = item.categories || []

  // Filter to only child categories (those with parent_hash !== null)
  const childCategories = getChildCategoriesUtil(categories)

  // If no child categories found, fall back to all categories
  const categoriesToUse = childCategories.length > 0 ? childCategories : categories

  return {
    item_id: 'id' in item ? item.id : item.hash,
    item_name: item_name,
    price: ('price' in item ? item.price : item.total) / 100,
    quantity: 1,
    item_variant: variation,
    ...(categoriesToUse[0] && { item_category: categoriesToUse[0] }),
    ...(categoriesToUse[1] && { item_category2: categoriesToUse[1] }),
    ...(categoriesToUse[2] && { item_category3: categoriesToUse[2] }),
    ...(categoriesToUse[3] && { item_category4: categoriesToUse[3] }),
    ...(categoriesToUse[4] && { item_category5: categoriesToUse[4] }),
    item_list_id: listInfo?.item_list_id,
    item_list_name: listInfo?.item_list_name
  }
}

function gtag() {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(arguments)
}

const logAll = (event: string, data: Record<string, any> = {}) => {
  data.timestamp = new Date().toISOString()
  // @ts-ignore
  gtag('event', event, data)

  if (!data.timestamp) {
    reportClientMessage(`Missing timestamp for ${event}`, { level: 'warn' })
  }

  window.parent.postMessage(
    {
      type: 'EVARO_ANALYTIC_EVENT',
      data: {
        event: `evaro_${event}`,
        data
      }
    },
    '*'
  )
}

export const logViewItem = (item: iProduct) => {
  logAll('view_item', {
    currency: 'GBP',
    item_name: item.title
  })
}

export const logViewItemList = (items: Array<iProduct | UpsellProduct>, options: { item_list_id: string; item_list_name: string }) => {
  logWithCategoriesReady(logAll, 'view_item_list', () => ({
    currency: 'GBP',
    items: items.map(item => formatItem(item, options))
  }))
}

export const logSelectItem = (item: iProduct | UpsellProduct, options: { item_list_id: string; item_list_name: string }) => {
  storeSelectedItem(item.hash, options.item_list_id, options.item_list_name)

  logWithCategoriesReady(logAll, 'select_item', () => ({
    currency: 'GBP',
    items: [formatItem(item, options)]
  }))
}

export const logAddToCart = (item: CartItem) => {
  logWithCategoriesReady(logAll, 'add_to_cart', () => ({
    currency: 'GBP',
    value: item.price / 100,
    items: [formatItem(item)]
  }))
}

export const logViewCart = () => {
  logWithCategoriesReady(logAll, 'view_cart', () => ({
    currency: 'GBP',
    value: useCheckoutStore.getState().getCartTotal() / 100,
    items: useCheckoutStore.getState().cart.map(item => formatItem(item))
  }))
}

export const logRemoveFromCart = (item: CartItem) => {
  logWithCategoriesReady(logAll, 'remove_from_cart', () => ({
    currency: 'GBP',
    value: item.price / 100,
    items: [formatItem(item)]
  }))
}

export const logBeginCheckout = () => {
  logWithCategoriesReady(logAll, 'begin_checkout', () => ({
    currency: 'GBP',
    value: useCheckoutStore.getState().getCartTotal() / 100,
    items: useCheckoutStore.getState().cart.map(item => formatItem(item)),
    coupon: useCheckoutStore.getState().coupon?.code
  }))
}

export const logPurchase = (order: any) => {
  const pid = useCheckoutStore.getState().email

  const itemHash = order.items[0]?.parent_hash || ''
  const cartItems = useCheckoutStore.getState().cart
  let value = Number.parseFloat(order.payment.total) / 100

  // Hardcode price for Free NHS Contraception treatment
  if (itemHash === '63ed36243b2603fa43737764017a66f4') {
    value = 25.0
    cartItems[0].price = 2500
  }
  logWithCategoriesReady(logAll, 'purchase', () => ({
    item_hash: order.items[0]?.parent_hash || '',
    transaction_id: order.meta.order_number,
    value: value,
    discount: order.payment.discount,
    sub_total: Number.parseFloat(order.payment.sub_total) / 100,
    shipping: Number.parseFloat(order.payment.shipping) / 100,
    shipping_method: order.shipping.method_title,
    currency: 'GBP',
    coupon: order.payment.coupons?.[0],
    ...(pid && { pid }),
    items: cartItems.map(item => formatItem(item))
  }))
  order.items.forEach(deleteStoredItem)
}

export const logOrderDetails = (totalOrders: number) => {
  logAll('order_details', {
    customer_type: totalOrders > 1 ? 'existing' : 'new',
    order_sequence_number: totalOrders
  })
}

export const logReorderButtonClicked = (hash: string, location?: 'header' | 'menu') => {
  logAll('reorder_button_clicked', {
    parent_hash: hash,
    ...(location && { button_location: location })
  })
}

export const logLogout = () => {
  logAll('logout', {
    login_status: 'logged_out'
  })
}

export const logLogin = (options?: { method?: string; login_place?: string }) => {
  logAll('login', {
    method: options?.method || 'email',
    login_status: 'logged_in',
    ...(options?.login_place && { login_place: options.login_place })
  })
}

export const logLoginError = (method: string, error: string) => {
  logAll('login_failed', {
    method,
    error_info: error
  })
}

export const logCouponError = (code: string, error: string) => {
  logAll('coupon_code_failed', {
    coupon: code,
    error_info: error
  })
}

export const logSignUp = (options?: { method?: string; login_place?: string }) => {
  logAll('sign_up', {
    method: options?.method || 'email',
    login_status: 'logged_in',
    ...(options?.login_place && { login_place: options.login_place })
  })
}

export const setUserId = (userId: string) => {
  // gtag("set", { user_id: userId });
}

export const logDarkMode = () => {
  const darkMode = window.matchMedia('(prefers-color-scheme: dark)').matches
  // @ts-ignore
  gtag('set', 'user_properties', {
    dark_mode: darkMode ? 'true' : 'false',
    incognito_mode: isStorageAvailable() ? 'false' : 'true'
  })
}

export const logScreenREsolution = () => {
  // @ts-ignore
  gtag('set', 'screen_resolution', `${window.screen.width}x${window.screen.height}`)
}

/**
 * What the patient is filling in, reported as consultation_type. Treatment, review, care and fan are the types an
 * author picks from; renew and reorder reach an existing one again. Renamed from main/renewal, and review and
 * care used to report as renewal — so a report split on this dimension breaks at this release.
 *
 * `questionnaireHash` is threaded rather than read off the store: a request-driven questionnaire leaves
 * `questionnaire_id` alone, so the store holds null or an unrelated consultation.
 */
export const logConsultationStarted = (
  experiment?: string,
  consultationType: iConsultationType = 'treatment',
  questionnaireHash = usePatientStore.getState().questionnaire_id
) => {
  logAll('consultation_started', {
    questionnaire_hash: questionnaireHash,
    experiment_of: experiment,
    consultation_type: consultationType
  })
}

export const logConsultationCompleted = (
  experiment?: string,
  consultationType: iConsultationType = 'treatment',
  questionnaireHash = usePatientStore.getState().questionnaire_id
) => {
  logAll('consultation_completed', {
    questionnaire_hash: questionnaireHash,
    experiment_of: experiment,
    consultation_type: consultationType
  })
}

export const logConsultationExited = (questionnaireHash = usePatientStore.getState().questionnaire_id) => {
  logAll('consultation_exited', {
    questionnaire_hash: questionnaireHash
  })
}

export const logExitSurveySubmitted = (reason: string, step: string | null, question?: string, recorded = true) => {
  logAll('exit_survey_submitted', {
    questionnaire_hash: usePatientStore.getState().questionnaire_id,
    reason,
    step,
    question_hash: question ?? null,
    recorded
  })
}

export const logQuestionViewed = (
  question: string,
  consultation: string,
  skipped: boolean = false,
  experiment?: string,
  consultationType: iConsultationType = 'treatment'
) => {
  logAll('question_viewed', {
    question_hash: question,
    consultation_hash: consultation,
    is_skipped: skipped,
    experiment_of: experiment,
    consultation_type: consultationType
  })
}

export const logQuestionAnswered = (
  question: string,
  consultation: string,
  skipped: boolean = false,
  experiment?: string,
  consultationType: iConsultationType = 'treatment'
) => {
  logAll('question_answered', {
    question_hash: question,
    consultation_hash: consultation,
    is_skipped: skipped,
    experiment_of: experiment,
    consultation_type: consultationType
  })
}

let prevPage = globalThis.document?.referrer ?? ''

export const logPageView = () => {
  window.parent.postMessage(
    {
      type: 'EVARO_ANALYTIC_EVENT',
      data: {
        event: 'evaro_page_view',
        data: {
          page_title: document.title,
          page_location: window.location.href,
          page_path: window.location.pathname,
          page_referrer: prevPage,
          timestamp: new Date().toISOString()
        }
      }
    },
    '*'
  )

  prevPage = window.location.href
}

export const logAddShippingInfo = () => {
  logAll('add_shipping_info', {
    shipping_type: useCheckoutStore.getState().shippingType?.title
  })
}

export const logAddPaymentInfo = (type: string) => {
  logAll('add_payment_info', {
    payment_type: type
  })
}

export const logViewMenu = () => {
  logAll('view_menu')
}

export const logMenuLinkClick = (link: Pick<ResolvedMenuLink, 'title' | 'href' | 'target' | 'external'>) => {
  logAll('menu_link_click', {
    link_text: link.title,
    link_url: link.href,
    link_target: link.target,
    outbound: link.external
  })
}

export const logViewLogin = () => {
  logAll('view_login')
}

export const logViewSignUp = () => {
  logAll('view_signup')
}

export const logViewForgotPassword = () => {
  logAll('view_forgot_password')
}

export const logViewLoginWithOTP = () => {
  logAll('view_login_with_otp')
}

export const logViewNewPassword = () => {
  logAll('view_new_password')
}

export const logViewOTP = () => {
  logAll('view_otp')
}

export const logProductChoice = (data: any) => {
  logAll('product_choice', data)
}

export const logOptionalLoginViewed = () => {
  logAll('optional_login_viewed')
}

export const logOptionalLoginInitiated = () => {
  logAll('optional_login_initiated')
}

export const logOptionalLoginDismissed = () => {
  logAll('optional_login_dismissed')
}

export const logDiscoveryOpened = () => {
  const hasSearch = new URLSearchParams(window.location.search).has('search')

  logAll('discovery_search_initiated', {
    source: hasSearch ? 'e-surgery' : 'connect'
  })
  askEvaroLogClient('Discovery Opened')
}

export const logDiscoveryClosed = () => {
  const hasSearch = new URLSearchParams(window.location.search).has('search')

  logAll('discovery_search_closed', {
    source: hasSearch ? 'e-surgery' : 'connect'
  })
  askEvaroLogClient('Discovery Closed')
}

export const logDiscoveryInputSubmitted = () => {
  const hasSearch = new URLSearchParams(window.location.search).has('search')

  logAll('discovery_query_submitted', {
    source: hasSearch ? 'e-surgery' : 'connect'
  })
}

export const logDiscoveryResetButton = () => {
  const hasSearch = new URLSearchParams(window.location.search).has('search')

  logAll('discovery_search_history_cleared', {
    source: hasSearch ? 'e-surgery' : 'connect'
  })
  return askEvaroLogClient('Reset button clicked')
}

export const logDiscoveryLinkClick = (text: string, data?: any) => {
  const hasSearch = new URLSearchParams(window.location.search).has('search')

  logAll('discovery_search_result_clicked', {
    source: hasSearch ? 'e-surgery' : 'connect'
  })
  askEvaroLogClient(text, data && JSON.stringify(data, null, 2))
}

export const logConsultationView = (number: number, all: number) => {
  logAll('consultation_viewed', {
    question_number: `Q-${number}`,
    question_number_of_total: `Q-${number} of ${all}`
  })
}

export const logConversationLinkClicked = (link: string) => {
  logAll('conversations_link_clicked', {
    link_url: link
  })
}

export const logRecoveredCartSuccess = () => {
  logAll('recovered_cart_success')
}

export const logRecoveredCartFailure = () => {
  logAll('recovered_cart_error')
}

export const logAppClosed = () => {
  logAll('app_closed')
}

// Consultation CTA tracking events
// cta_source separates the surfaces the card renders on; omitted for Ask Evaro,
// which reported these events before there was a second one.
export const logConsultationCTADisplayed = (categoryHash: string, questionnaire?: string, source?: string) => {
  logAll('consultation_cta_displayed', {
    category_hash: categoryHash,
    questionnaire_hash: questionnaire,
    cta_text: 'Unsure about your treatment options?',
    cta_type: 'consultation_action',
    ...(source && { cta_source: source })
  })
}

export const logConsultationCTAClicked = (categoryHash: string, questionnaire?: string, source?: string) => {
  logAll('consultation_cta_clicked', {
    category_hash: categoryHash,
    questionnaire_hash: questionnaire,
    cta_text: 'Start a consultation',
    cta_type: 'consultation_action',
    destination: `/questionnaire/${questionnaire}`,
    ...(source && { cta_source: source })
  })
}

// Helper function to manage selected items storage
const storeSelectedItem = (itemHash: string, listId: string, listName: string) => {
  try {
    const now = new Date()
    const item = {
      itemHash,
      listId,
      listName,
      timestamp: now.getTime()
    }

    localStorage.setItem(`selected_item_${itemHash}`, JSON.stringify(item))
  } catch (error) {
    console.warn('Failed to store selected item:', error)
  }
}

// Helper to get stored item info
export const getStoredItemListInfo = (item: any) => {
  try {
    let hash = 'id' in item ? item.id : item.hash
    let parentHash
    if ('parentId' in item) {
      parentHash = item.parentId
    } else if ('parent_hash' in item) {
      parentHash = item.parent_hash
    }
    let stored = localStorage.getItem(`selected_item_${hash}`)
    if (!stored) {
      hash = parentHash
      stored = localStorage.getItem(`selected_item_${hash}`)
    }

    const storedItem = JSON.parse(stored)
    const now = new Date().getTime()

    // Remove if older than 24 hours
    if (now - storedItem.timestamp > 24 * 60 * 60 * 1000) {
      localStorage.removeItem(`selected_item_${hash}`)
      return null
    }

    return {
      item_list_id: storedItem.listId,
      item_list_name: storedItem.listName
    }
  } catch {
    return null
  }
}

export const deleteStoredItem = (item: CartItem | iProduct) => {
  let hash = 'id' in item ? item.id : item.hash
  let parentHash = 'parentId' in item ? item.parentId : 'parent_hash' in item ? item.parent_hash : null
  if (hash) {
    localStorage.removeItem(`selected_item_${hash}`)
  }
  if (parentHash) {
    localStorage.removeItem(`selected_item_${parentHash}`)
  }
}

export const logMixedCartAlert = (conflictType: MixedCartConflictType) => {
  logAll('mixed_cart_alert_shown', {
    conflict_type: conflictType,
    cart_item_count: useCheckoutStore.getState().cart.length
  })
}

export const logTreatmentSelectionEDWLAsthma = (variant_name: 'new layout' | 'old layout') => {
  logAll('experiment_impression', {
    experiment_name: '202607 treatment selection for ED+WL+Asthma',
    variant_name: variant_name
  })
}

export const logPreConsultationPageABTest = (variant_name: 'with free text' | 'without free text') => {
  logAll('experiment_impression', {
    experiment_name: '202605 redesigned stepper',
    variant_name: variant_name
  })
}

export const logWlProductPageRedesign = (variant_name: 'new page' | 'old page') => {
  logAll('experiment_impression', {
    experiment_name: '202606 WL redesigned product page',
    variant_name: variant_name
  })
}

// patient-before-consult experiment
export const logPatientBeforeConsultation = (variant_name: 'patient first' | 'patient after') => {
  logAll('experiment_impression', {
    experiment_name: '202611 patient before consultation',
    variant_name: variant_name
  })
}

/** GA4 item list for the upsell modal opened by the given product. */
export const upsellModalList = (triggerTitle: string) => ({ item_list_id: 'upsell_modal', item_list_name: `Upsell Modal - ${triggerTitle}` })

export const logUpsellModalVariant = (variant_name: 'modal' | 'control') => {
  logAll('experiment_impression', {
    experiment_name: '202609 upsell modal',
    variant_name: variant_name
  })
}

export const logUpsellModalDismissed = (method: 'close' | 'checkout', added_count: number, trigger_hash: string) => {
  logAll('upsell_modal_dismissed', { method, added_count, trigger_hash })
}
